import { useCallback } from 'react';

import type { LtPlusBillingInterval, LtPlusCta } from '../../../types';
import { ANALYTICS_KEYS, BUCK_EVENTS } from '../../../utils/analytics';
import type { AnalyticsHandler } from '../../SignupDrawer/hooks/useSignupFlow';
import { VARIANT_CONTENT } from '../LtPlusDrawer.constants';

type VariantContent = (typeof VARIANT_CONTENT)[keyof typeof VARIANT_CONTENT];

type UseBenefitsStepOptions = {
  ltPlusCta: LtPlusCta;
  showSignInButton: boolean;
  billingInterval: LtPlusBillingInterval;
  onCheckoutClick: () => void;
  onSignInClick: () => void;
  onAnalyticsEvent?: AnalyticsHandler;
};

type UseBenefitsStepReturn = {
  variant: string;
  content: VariantContent;
  showPromoBox: boolean;
  promoTitle: string;
  promoDescription: string;
  promoDescriptionExtra?: string;
  benefitsHelperText: string;
  handleCheckoutClick: () => void;
  handleSignInClick: () => void;
};

/**
 * Encapsulates LT Plus benefits-step display values and interaction callbacks.
 */
export const useBenefitsStep = ({
  ltPlusCta,
  showSignInButton,
  billingInterval,
  onCheckoutClick,
  onSignInClick,
  onAnalyticsEvent,
}: UseBenefitsStepOptions): UseBenefitsStepReturn => {
  const variant = ltPlusCta.variant || 'general';
  const content = VARIANT_CONTENT[variant] || VARIANT_CONTENT.general;

  const promoTitle = ltPlusCta.title || content.promoTitle || '';
  const promoDescription = ltPlusCta.description || content.promoDescription || '';
  const promoDescriptionExtra = content.promoDescriptionExtra || '';
  const benefitsHelperText = content.benefitsHelperText || '';
  const showPromoBox = !!(content.showPromoBox && (promoTitle || promoDescription));

  const handleCheckoutClick = useCallback(() => {
    onAnalyticsEvent?.(BUCK_EVENTS.LT_PLUS_CHECKOUT_STARTED, {
      [ANALYTICS_KEYS.BILLING_INTERVAL]: billingInterval,
      [ANALYTICS_KEYS.VARIANT]: variant,
    });
    onCheckoutClick();
  }, [billingInterval, onAnalyticsEvent, onCheckoutClick, variant]);

  const handleSignInClick = useCallback(() => {
    if (!showSignInButton) {
      return;
    }

    onAnalyticsEvent?.(BUCK_EVENTS.LT_PLUS_SIGN_IN_CLICKED, {
      [ANALYTICS_KEYS.VARIANT]: variant,
    });
    onSignInClick();
  }, [onAnalyticsEvent, onSignInClick, showSignInButton, variant]);

  return {
    variant,
    content,
    showPromoBox,
    promoTitle,
    promoDescription,
    promoDescriptionExtra,
    benefitsHelperText,
    handleCheckoutClick,
    handleSignInClick,
  };
};
